import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { api } from "../lib/api";
import { VRIJWILLIGERS_API } from "../lib/contactApiPaths";
import {
  autoMapFromHeaders,
  importFieldDefsForApiPath,
  importMappingIsComplete,
  importRowHasIdentity,
  isOrgContactImportPath,
  mapRowToObject,
} from "../lib/contactImportSheet";
import type { Group } from "@domaddress/shared";

type DupMode = "insert" | "skip" | "merge";

export default function ImportContactsPage({ apiPath = VRIJWILLIGERS_API }: { apiPath?: string } = {}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawData, setRawData] = useState<unknown[][]>([]);

  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [dupMode, setDupMode] = useState<DupMode>("insert");
  /** Vrije tag op alle kaarten die deze run nieuw aanmaakt of samenvoegt (niet bij “overslaan”). */
  const [importLabel, setImportLabel] = useState("");
  const [existingLabels, setExistingLabels] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldDefs = importFieldDefsForApiPath(apiPath);
  const orgImport = isOrgContactImportPath(apiPath);

  useEffect(() => {
    api.get("/api/groups").then((res) => setGroups(res.data.data)).catch(console.error);
  }, []);

  useEffect(() => {
    api
      .get<{ data: string[] }>("/api/labels")
      .then((res) => setExistingLabels(res.data.data ?? []))
      .catch(() => setExistingLabels([]));
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        if (typeof bstr !== "string") {
          setError("Bestand kon niet als tekst worden gelezen.");
          return;
        }
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 });

        if (data.length < 2) {
          setError("Het bestand bevat onvoldoende rijen (minimaal een kopregel + één gegevensrij).");
          return;
        }

        // BOM strippen zodat kolomnamen matchen (Excel exporteert soms \uFEFF voor "Voornaam").
        const firstRow = data[0].map((h) => String(h ?? "").replace(/^\uFEFF/, "").trim());
        setHeaders(firstRow);
        setRawData(data.slice(1) as unknown[][]);
        setMappings(autoMapFromHeaders(firstRow, fieldDefs));
        setStep(2);
        setError(null);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(`Lezen van het bestand is mislukt: ${msg}`);
      }
    };
    reader.readAsBinaryString(f);
  };

  const executeImport = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!importMappingIsComplete(mappings, apiPath)) {
        throw new Error(
          orgImport
            ? "Koppel minstens één kolom: voornaam, achternaam, bedrijfsnaam, e-mail of telefoon."
            : "Koppel minstens de kolommen “Voornaam” en “Achternaam”."
        );
      }

      const payload = rawData
        .map((row) => mapRowToObject(headers, mappings, row as unknown[]))
        .filter((c) => importRowHasIdentity(c));

      if (payload.length === 0) {
        throw new Error(
          orgImport
            ? "Geen geldige rijen om te importeren (minstens bedrijfsnaam, e-mail, telefoon of een deel van de naam ontbreekt overal)."
            : "Geen geldige rijen om te importeren (voornaam of achternaam ontbreekt)."
        );
      }

      const res = await api.post(`${apiPath}/import`, {
        contacts: payload,
        group_id: selectedGroupId ? Number(selectedGroupId) : null,
        on_duplicate: dupMode,
        ...(importLabel.trim() ? { import_label: importLabel.trim() } : {}),
      });
      const d = res.data as {
        count?: number;
        imported: number;
        merged: number;
        skipped: number;
        skippedNoPermission?: number;
        inFileSkipped?: number;
        /** Rijen die niet geldig waren (controle op basis van het schema). */
        parseFailed?: number;
      };
      const parts = [
        d.imported > 0 ? `nieuw: ${d.imported}` : null,
        d.merged > 0 ? `samengevoegd: ${d.merged}` : null,
        d.skipped > 0 ? `overgeslagen (stond al in de database): ${d.skipped}` : null,
        d.skippedNoPermission && d.skippedNoPermission > 0
          ? `niet toegestaan: ${d.skippedNoPermission}`
          : null,
        d.inFileSkipped && d.inFileSkipped > 0 ? `dubbel in bestand: ${d.inFileSkipped}` : null,
        d.parseFailed && d.parseFailed > 0
          ? `niet verwerkt (onvolledig of ongeldige gegevens in rij): ${d.parseFailed}`
          : null,
      ].filter(Boolean);
      const summary = parts.length > 0 ? parts.join(" · ") : "Geen wijzigingen geregistreerd.";
      const tagNote =
        importLabel.trim() && ((d.imported ?? 0) > 0 || (d.merged ?? 0) > 0)
          ? `\nTag “${importLabel.trim()}” toegevoegd op nieuwe en samengevoegde kaarten.`
          : "";
      alert(`Import klaar.\n${summary}${tagNote}`);
      navigate("/medewerkers");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error ?? (err instanceof Error ? err.message : "Importeren is mislukt"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button type="button" className="btn-back" onClick={() => navigate("/medewerkers")}>
            ← Terug
          </button>
          <h2 className="page-title">Medewerkers importeren</h2>
          <p className="page-subtitle">Stap {step} van 3</p>
        </div>
      </div>

      <div className="form-card" style={{ maxWidth: 800, margin: "0 auto", padding: "2rem" }}>
        {error && (
          <div
            className="error-banner"
            style={{ background: "var(--danger)", color: "white", padding: "1rem", marginBottom: "1rem", borderRadius: 4 }}
            role="alert"
          >
            {error}
          </div>
        )}

        {step === 1 && (
          <div className="import-step" style={{ textAlign: "center" }}>
            <h3>Excel- of csv-bestand</h3>
            <p style={{ margin: "1rem 0" }}>
              Eerste regel = kolomnamen. Ondersteunde formaten: <strong>.xlsx</strong>, <strong>.xls</strong> of <strong>
                .csv
              </strong>
              . Daarna koppel je elke kolom aan een veld in Adresboek Dominicus (ook alle nieuwe medewerker-velden).
            </p>
            <p style={{ marginBottom: "1rem", fontSize: "0.9rem", color: "var(--muted)" }}>
              <strong>Duplicaten</strong> herkennen we op <strong>zelfde e-mail (privé)</strong>, anders op{" "}
              <strong>voornaam + achternaam</strong>. In stap 3 kies je of we dubbels negeren, overslaan of de bestaande
              contactpersoon <strong>aanvullen</strong> (samenvoegen) met de gegevens uit het bestand. Bij modus
              <em> Nieuwe regels, geen controle</em> wordt elke rij als nieuw contact aangemaakt (dubbels in de database
              mogelijk).
            </p>
            <p style={{ marginBottom: "1rem", fontSize: "0.9rem", color: "var(--muted)" }}>
              <strong>Vrije tag bij import:</strong> in stap 3 kun je optioneel één label invullen (bijv.{" "}
              <code>import_excel_2025-04</code>) dat op alle <em>nieuwe</em> en <em>samengevoegde</em> kaarten uit deze run
              wordt gezet — handig om later te zien wat uit welk bestand kwam. Bestaande kaarten die je overslaat, krijgen
              die tag niet; voeg die eventueel handmatig toe via de contactkaart (vrije tags).
            </p>
            <input
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <button type="button" className="btn-primary" onClick={() => fileInputRef.current?.click()}>
              Bestand kiezen
            </button>
            {file && <p style={{ marginTop: 12, fontSize: "0.9rem" }}>Geselecteerd: {file.name}</p>}
          </div>
        )}

        {step === 2 && (
          <div className="import-step">
            <h3>Kolommen koppelen</h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Kies per veld de bijpassende kolom uit je bestand, of <strong>Negeren</strong> als de kolom in Adresboek Dominicus niet
              thuishoort.
            </p>

            <div style={{ display: "grid", gap: "1rem" }}>
              {fieldDefs.map((field) => (
                <div
                  key={field.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    padding: "1rem",
                    background: "var(--surface)",
                    borderRadius: 6,
                  }}
                >
                  <div>
                    <strong>{field.label}</strong>
                    {field.required && (
                      <span className="data-list-row-muted" style={{ fontSize: "0.8rem" }}>
                        {" "}
                        (verplicht te koppelen)
                      </span>
                    )}
                  </div>
                  <select
                    style={{ minWidth: 250, maxWidth: "100%", padding: "0.5rem", borderRadius: 4, border: "1px solid var(--border)" }}
                    value={mappings[field.key] || ""}
                    onChange={(e) => setMappings({ ...mappings, [field.key]: e.target.value })}
                    aria-label={`Koppeling voor ${field.label}`}
                  >
                    <option value="">— Negeren —</option>
                    {headers.map((h, i) => (
                      <option key={i} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "2rem", display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
                Vorige
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!importMappingIsComplete(mappings, apiPath)}
                onClick={() => setStep(3)}
              >
                Volgende
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="import-step">
            <h3>Laatste stappen</h3>
            <p style={{ marginBottom: "1rem" }}>
              Klaar om ongeveer <strong>{rawData.length}</strong> te verwerken (lege regels en rijen zonder minstens{" "}
              {orgImport
                ? "bedrijfsnaam, e-mail, telefoon of naam"
                : "voornaam of achternaam"}{" "}
              tellen niet mee in het resultaat).
            </p>

            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Bij dezelfde e-mail, ofzelfde naam zonder e-mail in de app</p>
              <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                <label className="contact-pick-item" style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="dup"
                    checked={dupMode === "insert"}
                    onChange={() => setDupMode("insert")}
                  />
                  <span>
                    <strong>Altijd nieuw contact</strong> — geen controle op doublures (kan dubbele personen opleveren)
                  </span>
                </label>
                <label className="contact-pick-item" style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="dup"
                    checked={dupMode === "skip"}
                    onChange={() => setDupMode("skip")}
                  />
                  <span>
                    <strong>Bestaande overgeslagen</strong> — regel telt mee in “overgeslagen” als de persoon al in de
                    database staat
                  </span>
                </label>
                <label className="contact-pick-item" style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="dup"
                    checked={dupMode === "merge"}
                    onChange={() => setDupMode("merge")}
                  />
                  <span>
                    <strong>Samenvoegen (aanvullen)</strong> — bestaand contact aanvullen met ingevulde cellen uit het
                    importbestand (rechten volgens beheer, zoals bij bewerken)
                  </span>
                </label>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <label htmlFor="import-label">Vrije tag op import (optioneel)</label>
              <input
                id="import-label"
                type="text"
                className="filter-select"
                style={{ width: "100%", maxWidth: "100%" }}
                list="import-label-suggestions"
                value={importLabel}
                onChange={(e) => setImportLabel(e.target.value)}
                placeholder="bijv. import_excel_april of kies een bestaande tag uit de lijst"
                maxLength={80}
                autoComplete="off"
              />
              <datalist id="import-label-suggestions">
                {existingLabels.map((lbl) => (
                  <option key={lbl} value={lbl} />
                ))}
              </datalist>
              <p style={{ fontSize: "0.82rem", color: "var(--muted)", marginTop: 6, marginBottom: 0 }}>
                Alleen op kaarten die nu <strong>nieuw</strong> worden aangemaakt of <strong>samengevoegd</strong> worden;
                niet bij “bestaande overslaan”. Je kunt hier elke nieuwe naam typen — die verschijnt daarna ook in de
                suggestielijst.
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: "2rem" }}>
              <label htmlFor="import-group">Groep (optioneel)</label>
              <select
                id="import-group"
                className="filter-select"
                style={{ width: "100%" }}
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
              >
                <option value="">— Geen groep, alleen in het algemene medewerkersbestand —</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: "2rem", display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
                Vorige
              </button>
              <button type="button" className="btn-primary" onClick={executeImport} disabled={saving}>
                {saving ? "Bezig met importeren…" : "Importeren"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
